/**
 * Pareamento área↔responsável das células Responsável/Área do Resumo (pedido
 * 08/09, screenshots): as duas colunas traziam listas SOLTAS — não dava para
 * saber qual responsável era de qual área. Aqui vira lista de PARES ordenados,
 * que a tabela renderiza como pilhas verticais ALINHADAS (linha N de uma coluna
 * = par N da outra).
 *
 * FONTE por linha (contrato 08/09): `linha.areas` do `/resumo` (cadastro, só as
 * 3 áreas essenciais preenchidas) é a fonte PRIMÁRIA — cobre empresa em
 * processamento sem execução ativa, que antes mostrava travessão; sem o campo
 * (BFF antigo, degradação nula), fallback no mapa das execuções ativas
 * (`responsaveis[codi_emp]`), o comportamento de sempre.
 */

import type { IEmpresaResumo, IResponsaveisPorEmpresa } from '../api';

export interface ParAreaResponsavel {
  area: string;
  responsavel: string;
}

/** Ordem FIXA das áreas essenciais nos pares; extras vão depois, alfabéticas. */
const POSICAO_ESSENCIAL: Record<string, number> = { contabil: 0, fiscal: 1, pessoal: 2 };

/**
 * Mapa `{ area: responsável }` da LINHA: `linha.areas` quando presente E com
 * conteúdo (o contrato só manda áreas preenchidas — `{}` vale como ausente);
 * senão o fallback das execuções ativas. Undefined = nenhuma fonte (travessão).
 */
export const areasDaLinha = (
  linha: Pick<IEmpresaResumo, 'codi_emp' | 'areas'>,
  responsaveis: IResponsaveisPorEmpresa | undefined,
): Record<string, string> | undefined =>
  linha.areas && Object.keys(linha.areas).length ? linha.areas : responsaveis?.[linha.codi_emp];

/**
 * Pares ordenados: contabil → fiscal → pessoal (ordem fixa das essenciais) e as
 * demais áreas depois, em ordem alfabética — a MESMA ordem nas duas pilhas é o
 * que faz o alinhamento visual casar.
 */
export const paresAreaResponsavel = (
  mapa: Record<string, string> | undefined,
): ParAreaResponsavel[] =>
  Object.entries(mapa ?? {})
    .map(([area, responsavel]) => ({ area, responsavel }))
    .sort((a, b) => {
      const pa = POSICAO_ESSENCIAL[a.area] ?? Number.POSITIVE_INFINITY;
      const pb = POSICAO_ESSENCIAL[b.area] ?? Number.POSITIVE_INFINITY;
      if (pa !== pb) return pa - pb;
      return a.area.localeCompare(b.area, 'pt-BR');
    });

/** Tooltip das células: os pares COMPLETOS, "área: responsável", um por linha. */
export const tooltipDosPares = (pares: ParAreaResponsavel[]): string =>
  pares.map(p => `${p.area}: ${p.responsavel}`).join('\n');
